<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useKanbanPermissions } from 'dashboard/composables/useKanbanPermissions';
import MiniDealCard from './MiniDealCard.vue';
import CreateDealDrawer from './CreateDealDrawer.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  contactId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const currentAccountId = useMapGetter('getCurrentAccountId');
const { canManageDeals } = useKanbanPermissions();

const showCreateDrawer = ref(false);

const deals = computed(() =>
  store.getters['kanban/dealsForConversation'](props.conversationId)
);
const pipelines = computed(() => store.getters['kanban/pipelines']);
const dealCount = computed(() => deals.value.length);

const loadDeals = conversationId => {
  if (!conversationId) return;
  store.dispatch('kanban/fetchConversationDeals', conversationId);
};

onMounted(() => {
  loadDeals(props.conversationId);
  if (!pipelines.value.length) {
    store.dispatch('kanban/fetchPipelines');
  }
});

watch(
  () => props.conversationId,
  newId => {
    loadDeals(newId);
    showCreateDrawer.value = false;
  }
);

const toggleCreateDrawer = () => {
  showCreateDrawer.value = !showCreateDrawer.value;
};

const closeCreateDrawer = () => {
  showCreateDrawer.value = false;
};

const navigateToDeal = deal => {
  router.push({
    path: `/app/accounts/${currentAccountId.value}/kanban`,
    query: { deal_id: deal.id },
  });
};
</script>

<template>
  <div class="flex flex-col gap-2 px-3 py-2">
    <header class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('KANBAN.DEAL_PANEL.TITLE') }}
        </h3>
        <span
          v-if="dealCount > 0"
          class="text-[11px] font-medium px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11"
        >
          {{ dealCount }}
        </span>
      </div>
      <button
        v-if="canManageDeals"
        type="button"
        class="w-6 h-6 flex items-center justify-center rounded-md text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="t('KANBAN.DEAL_PANEL.EMPTY_CTA')"
        @click="toggleCreateDrawer"
      >
        <span class="i-lucide-plus text-base" />
      </button>
    </header>

    <div
      v-if="!dealCount && !showCreateDrawer"
      class="flex flex-col items-center gap-2 py-4 text-center"
    >
      <p class="text-xs text-n-slate-11">
        {{ t('KANBAN.DEAL_PANEL.EMPTY_TITLE') }}
      </p>
      <button
        type="button"
        class="text-xs font-semibold px-3 py-1.5 rounded-md bg-n-brand text-white hover:opacity-90"
        @click="toggleCreateDrawer"
      >
        {{ t('KANBAN.DEAL_PANEL.EMPTY_CTA') }}
      </button>
    </div>

    <div v-else-if="dealCount" class="flex flex-col gap-2">
      <MiniDealCard
        v-for="deal in deals"
        :key="deal.id"
        :deal="deal"
        @click="navigateToDeal(deal)"
      />
    </div>

    <CreateDealDrawer
      v-if="showCreateDrawer"
      :conversation-id="conversationId"
      :contact-id="contactId"
      @created="loadDeals(conversationId)"
      @close="closeCreateDrawer"
    />
  </div>
</template>
